import { useCallback, useEffect, useId, useRef, useState } from "react";
import clsx from "clsx";

import { errorMessage } from "../lib/api";
import { usePatchEntry } from "../lib/queries";
import type { Entry, EntryPatch } from "../lib/types";

const DEBOUNCE_MS = 800;

type SaveState = "idle" | "saving" | "saved" | "error";

/**
 * Autosave: debounced while typing, flushed on blur, and flushed again if the
 * component unmounts with unsaved text. The mutation itself is optimistic, so
 * the words never appear to jump about.
 */
function useAutosave(entry: Entry, build: (value: string) => EntryPatch, initial: string) {
  const patch = usePatchEntry(entry.id);
  const [value, setValue] = useState(initial);
  const [state, setState] = useState<SaveState>("idle");
  const [problem, setProblem] = useState<string | null>(null);

  const saved = useRef(initial);
  const timer = useRef<number | null>(null);
  const focused = useRef(false);

  // Accept changes that arrive from the server (a retry, another tab) unless
  // the reader is mid-sentence.
  useEffect(() => {
    if (focused.current) return;
    if (initial === saved.current) return;
    saved.current = initial;
    setValue(initial);
  }, [initial]);

  const flush = useCallback(
    (next: string) => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
      if (next === saved.current) return;

      saved.current = next;
      setState("saving");
      setProblem(null);

      patch.mutate(build(next), {
        onSuccess: () => setState("saved"),
        onError: (error) => {
          setState("error");
          setProblem(errorMessage(error, "That didn't save."));
        },
      });
    },
    [build, patch],
  );

  const flushRef = useRef(flush);
  flushRef.current = flush;

  const valueRef = useRef(value);
  valueRef.current = value;

  const onChange = useCallback((next: string) => {
    setValue(next);
    setState("idle");
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => flushRef.current(next), DEBOUNCE_MS);
  }, []);

  const onFocus = useCallback(() => {
    focused.current = true;
  }, []);

  const onBlur = useCallback(() => {
    focused.current = false;
    flushRef.current(valueRef.current);
  }, []);

  // Don't lose a half-written note to a navigation.
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      flushRef.current(valueRef.current);
    },
    [],
  );

  // "Saved" is a reassurance, not a permanent label.
  useEffect(() => {
    if (state !== "saved") return;
    const timeout = window.setTimeout(() => setState("idle"), 2400);
    return () => window.clearTimeout(timeout);
  }, [state]);

  return { value, state, problem, onChange, onFocus, onBlur };
}

function SaveHint({ state, problem }: { state: SaveState; problem: string | null }) {
  if (state === "idle") return null;
  return (
    <p
      role="status"
      aria-live="polite"
      className={clsx(
        "field-label-tight",
        state === "error" ? "text-rust" : state === "saved" ? "text-moss" : "text-ink-soft",
      )}
    >
      {state === "saving" ? "Saving…" : state === "saved" ? "Saved" : (problem ?? "Not saved")}
    </p>
  );
}

/* -------------------------------------------------------------------------- */

export type FieldNoteProps = {
  entry: Entry;
  className?: string;
};

/**
 * Your own hand on the sheet. Ruled paper, a serif face, and no save button —
 * it keeps up with you.
 */
export function FieldNote({ entry, className }: FieldNoteProps) {
  const id = useId();
  const buildPatch = useCallback(
    (value: string): EntryPatch => ({ notes: value.trim().length > 0 ? value : null }),
    [],
  );
  const { value, state, problem, onChange, onFocus, onBlur } = useAutosave(
    entry,
    buildPatch,
    entry.notes ?? "",
  );

  return (
    <section className={clsx("sheet grain relative p-4 sm:p-5", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="field-label text-ink">
          Your field note
        </label>
        <SaveHint state={state} problem={problem} />
      </div>

      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        rows={5}
        spellCheck
        placeholder="What was it like to come across this? Weather, company, what you were doing there…"
        aria-describedby={`${id}-help`}
        className="ruled-paper mt-3 block w-full resize-y border-0 bg-transparent px-0 font-prose text-[1.0625rem] text-ink placeholder:text-ink-soft/60 focus:outline-none"
      />

      <p id={`${id}-help`} className="mt-2 font-prose text-sm text-ink-soft">
        Saved as you write. Your notes are searchable.
      </p>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

export type TitleFieldProps = {
  entry: Entry;
  className?: string;
};

/**
 * Renaming a find. Claude's determination stays on the record underneath; this
 * only sets the name you call it by.
 */
export function TitleField({ entry, className }: TitleFieldProps) {
  const id = useId();
  const buildPatch = useCallback(
    (value: string): EntryPatch => ({ userTitle: value.trim().length > 0 ? value.trim() : null }),
    [],
  );
  const { value, state, problem, onChange, onFocus, onBlur } = useAutosave(
    entry,
    buildPatch,
    entry.userTitle ?? "",
  );

  const given = entry.title ?? entry.commonName;

  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="field-label text-ink">
          Call it something else
        </label>
        <SaveHint state={state} problem={problem} />
      </div>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={given ?? "Your name for this find"}
        className="mt-2 block w-full border-0 border-b border-rule bg-transparent px-0 py-1.5 display-6 text-xl text-ink placeholder:font-prose placeholder:text-base placeholder:text-ink-soft/60 focus:border-ochre focus:outline-none"
      />
      {given && entry.userTitle ? (
        <p className="mt-1.5 font-prose text-sm text-ink-soft">
          Originally identified as <span className="italic">{given}</span>.
        </p>
      ) : null}
    </div>
  );
}
